import { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { ActionHandler } from "@/components/UI/tools";
import { appState } from "@/app/appState";
import { client } from "@/api/client.gen";
import { Add_new, ArrowDown, ArrowRight } from "@/components/UI/svgs";
import { buildKeyTree, flattenKeyTree } from "@/app/workspace/settings/keyTree";
import { kvpLeavesForI18nKey, skillGroupIdFromPath } from "@/app/workspace/kvp/kvpRouteSpecs";
import { SettingsKeyLinks } from "@/app/workspace/kvp/kvpSideEffectsEditor";
import { Pill, settingsRowId } from "@/app/workspace/settings/ui";
import { EXPECTED_I18N_KEYS, EXPECTED_I18N_KEY_SET } from "./i18nKeyRegistry";

type LocaleInfo = { code: string; name: string; is_default: boolean };
type I18nEntry = { key: string; values: Record<string, string> };

const INDENT_PER_LEVEL = 14;
const emptyValues = (locales: LocaleInfo[]): Record<string, string> =>
    Object.fromEntries(locales.map((locale) => [locale.code, ""]));

function mergeExpectedI18n(
    db: I18nEntry[],
    locales: LocaleInfo[],
): { entries: I18nEntry[]; persistedKeys: Set<string> } {
    // Union DB rows with i18nKeyRegistry. Stubs are local until Save.
    const persistedKeys = new Set(db.map((entry) => entry.key));
    const byKey = new Map(db.map((entry) => [entry.key, entry]));
    const blanks = emptyValues(locales);
    for (const key of EXPECTED_I18N_KEYS) {
        if (!byKey.has(key)) {
            byKey.set(key, { key, values: { ...blanks } });
        }
    }
    return {
        entries: [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key)),
        persistedKeys,
    };
}

export const useI18nAdmin = () => {
    const [locales, setLocales] = useState<LocaleInfo[]>([]);
    const [entries, setEntries] = useState<I18nEntry[]>([]);
    const [persistedKeys, setPersistedKeys] = useState<Set<string>>(new Set());
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [originalKey, setOriginalKey] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isSuperAdmin = !!appState.user?.is_super_admin;

    const selected = selectedKey == null
        ? null
        : entries.find((entry) => entry.key === selectedKey) ?? null;

    const load = async (preserveKey?: string | null) => {
        setLoading(true);
        setError(null);
        try {
            const response = await client.post({
                url: "/api/i18n/get-all",
                body: {},
                headers: { "Content-Type": "application/json" },
            });
            const data = response.data as {
                locales?: LocaleInfo[];
                entries?: I18nEntry[];
            } | undefined;
            if (data?.entries && data.locales) {
                const { entries: merged, persistedKeys: nextPersisted } = mergeExpectedI18n(
                    data.entries,
                    data.locales,
                );
                setLocales(data.locales);
                setEntries(merged);
                setPersistedKeys(nextPersisted);
                const nextKey =
                    preserveKey !== undefined
                        ? (preserveKey && merged.some((entry) => entry.key === preserveKey)
                            ? preserveKey
                            : null)
                        : (selectedKey && merged.some((entry) => entry.key === selectedKey)
                            ? selectedKey
                            : null);
                setSelectedKey(nextKey);
                setOriginalKey(nextKey);
            } else {
                setError(
                    (response.error as { detail?: string })?.detail ??
                    appState.t("chat.requestFailed"),
                );
            }
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : appState.t("chat.requestFailed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isSuperAdmin) load();
    }, [isSuperAdmin]);

    const selectKey = useCallback((key: string) => {
        setSelectedKey(key);
        setOriginalKey(key);
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedKey(null);
        setOriginalKey(null);
    }, []);

    const updateSelectedKey = (key: string) => {
        if (selectedKey == null) return;
        setEntries((prev) =>
            prev.map((entry) =>
                entry.key === selectedKey ? { ...entry, key } : entry,
            ),
        );
        setSelectedKey(key);
    };

    const updateSelectedValue = (locale: string, value: string) => {
        if (selectedKey == null) return;
        setEntries((prev) =>
            prev.map((entry) =>
                entry.key === selectedKey
                    ? { ...entry, values: { ...entry.values, [locale]: value } }
                    : entry,
            ),
        );
    };

    const addRow = () => {
        const draft: I18nEntry = { key: "", values: emptyValues(locales) };
        setEntries((prev) => [...prev, draft]);
        setSelectedKey("");
        setOriginalKey("");
    };

    const ensureKey = useCallback(async (key: string) => {
        const trimmed = key.trim();
        if (!trimmed) return;

        const response = await client.post({
            url: "/api/i18n/get-all",
            body: {},
            headers: { "Content-Type": "application/json" },
        });
        const data = response.data as {
            locales?: LocaleInfo[];
            entries?: I18nEntry[];
        } | undefined;
        if (!data?.entries || !data.locales) {
            appState.setModal({
                title: appState.t("common.error"),
                body:
                    (response.error as { detail?: string })?.detail ??
                    appState.t("common.save"),
            });
            return;
        }
        const { entries: merged, persistedKeys: nextPersisted } = mergeExpectedI18n(
            data.entries,
            data.locales,
        );
        if (!merged.some((entry) => entry.key === trimmed)) {
            merged.push({ key: trimmed, values: emptyValues(data.locales) });
        }
        setLocales(data.locales);
        setEntries(merged);
        setPersistedKeys(nextPersisted);
        setSelectedKey(trimmed);
        setOriginalKey(trimmed);
    }, []);

    const saveSelected = async () => {
        if (!selected?.key.trim()) {
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("settings.kvp.keyEmpty"),
            });
            return;
        }

        setSaving(true);
        try {
            const response = await client.post({
                url: "/api/i18n/set",
                body: {
                    key: selected.key.trim(),
                    values: selected.values,
                    previous_key: originalKey && persistedKeys.has(originalKey)
                        ? originalKey
                        : null,
                },
                headers: { "Content-Type": "application/json" },
            });
            if (response.error) {
                appState.setModal({
                    title: appState.t("common.error"),
                    body:
                        (response.error as { detail?: string })?.detail ??
                        appState.t("common.save"),
                });
                return;
            }
            const savedKey = selected.key.trim();
            await load(savedKey);
            await appState.loadStrings(appState.locale);
        } catch (e: unknown) {
            appState.setModal({
                title: appState.t("common.error"),
                body: e instanceof Error ? e.message : appState.t("common.save"),
            });
        } finally {
            setSaving(false);
        }
    };

    const deleteSelected = (after?: () => void) => {
        if (selectedKey == null || !selected) return;
        const keyToDelete = originalKey?.trim() || selected.key.trim();

        const run = async () => {
            const isPersisted = !!(keyToDelete && persistedKeys.has(keyToDelete));
            const isExpected = EXPECTED_I18N_KEY_SET.has(keyToDelete);

            if (keyToDelete && !isPersisted && isExpected) return;

            if (isPersisted) {
                setSaving(true);
                try {
                    const response = await client.post({
                        url: "/api/i18n/delete",
                        body: { key: keyToDelete },
                        headers: { "Content-Type": "application/json" },
                    });
                    if (response.error) {
                        appState.setModal({
                            title: appState.t("common.error"),
                            body:
                                (response.error as { detail?: string })?.detail ??
                                appState.t("common.delete"),
                        });
                        return;
                    }
                } catch (e: unknown) {
                    appState.setModal({
                        title: appState.t("common.error"),
                        body: e instanceof Error ? e.message : appState.t("common.delete"),
                    });
                    return;
                } finally {
                    setSaving(false);
                }
                setSelectedKey(null);
                setOriginalKey(null);
                after?.();
                await load(null);
                await appState.loadStrings(appState.locale);
                return;
            }

            setEntries((prev) => prev.filter((entry) => entry.key !== selectedKey));
            setSelectedKey(null);
            setOriginalKey(null);
            after?.();
        };

        if (!keyToDelete) {
            run();
            return;
        }

        appState.setModal({
            title: appState.t("common.confirmTitle"),
            body: appState.t("settings.i18n.deleteBody"),
            action: () => { void run(); },
        });
    };

    return {
        isSuperAdmin,
        locales,
        entries,
        persistedKeys,
        selectedKey,
        selected,
        originalKey,
        loading,
        saving,
        error,
        load,
        selectKey,
        clearSelection,
        updateSelectedKey,
        updateSelectedValue,
        addRow,
        ensureKey,
        saveSelected,
        deleteSelected,
    };
};

export type I18nAdminState = ReturnType<typeof useI18nAdmin>;

export const I18nTree = observer(({
    i18n,
    search,
    setSearch,
    expanded,
    toggleExpanded,
    onSelect,
    onAdd,
    onOpenKvp,
}: {
    i18n: I18nAdminState;
    search: string;
    setSearch: (val: string) => void;
    expanded: Set<string>;
    toggleExpanded: (path: string) => void;
    onSelect: (key: string) => void;
    onAdd: () => void;
    onOpenKvp?: (key: string) => void;
}) => {
    const [missingOnly, setMissingOnly] = useState(false);
    const visibleKeys = useMemo(() => {
        const keys = i18n.entries.map((entry) => entry.key).filter(Boolean);
        if (!missingOnly) return keys;
        return keys.filter(
            (key) => EXPECTED_I18N_KEY_SET.has(key) && !i18n.persistedKeys.has(key),
        );
    }, [i18n.entries, i18n.persistedKeys, missingOnly]);
    const roots = useMemo(
        () => buildKeyTree(visibleKeys),
        [visibleKeys],
    );
    const rows = flattenKeyTree(roots, { expanded, search, expandAll: missingOnly });
    const hasDraft = !missingOnly && i18n.entries.some((entry) => entry.key === "");

    return (
        <div className="flex flex-col gap-[.2rem] min-h-0">
            <label 
                onClick={(e) => {
                    e.stopPropagation();
                    setMissingOnly(!missingOnly);
                }}
                className={`filter-toggle px-[.85rem] pb-[.35rem] ${missingOnly ? "filter-toggle-active" : ""}`}>
                <span>
                    {appState.t("settings.i18n.missing")}
                </span>
            </label>
            <div className="px-[.35rem] pb-[.35rem] flex flex-row items-center gap-[.25rem]">
                <input
                    className="search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={appState.t("common.filterPlaceholder")}
                />
                <ActionHandler
                    title={appState.t("settings.i18n.add")}
                    funq={onAdd}
                    disabled={i18n.loading || i18n.saving}
                    className="btn-ghost"
                >
                    <Add_new size={14} />
                </ActionHandler>
            </div>
            

            {i18n.loading && rows.length === 0 && (
                <p className="tree-empty">{appState.t("common.loading")}</p>
            )}
            {i18n.error && (
                <p className="error-text px-[.5rem]">{i18n.error}</p>
            )}
            {!i18n.loading && rows.length === 0 && !hasDraft && (
                <p className="tree-empty">
                    {search.trim()
                        ? appState.t("settings.i18n.noMatch")
                        : appState.t("settings.i18n.empty")}
                </p>
            )}

            {hasDraft && (
                <div
                    onClick={() => onSelect("")}
                    className={`tree-node pl-[.5rem] ${i18n.selectedKey === "" ? "tree-node-selected" : ""}`}
                >
                    <span className="tree-node-label tree-node-label-mono tree-node-label-muted">
                        {appState.t("settings.i18n.newKey")}
                    </span>
                </div>
            )}

            {rows.map(({ node, depth, hasChildren }) => {
                const isSelected = node.isKey && node.path === i18n.selectedKey;
                const unregistered = node.isKey
                    && EXPECTED_I18N_KEY_SET.has(node.path)
                    && !i18n.persistedKeys.has(node.path);
                const unsaved = node.isKey
                    && !i18n.persistedKeys.has(node.path)
                    && !EXPECTED_I18N_KEY_SET.has(node.path);
                const skillId = skillGroupIdFromPath(node.path);

                return (
                    <div
                        key={node.path}
                        id={settingsRowId("i18n", node.path)}
                        onClick={() => {
                            if (node.isKey) onSelect(node.path);
                            else if (hasChildren) toggleExpanded(node.path);
                        }}
                        style={{ paddingLeft: depth * INDENT_PER_LEVEL }}
                        className={`tree-node ${isSelected ? "tree-node-selected" : ""}`}
                    >
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hasChildren) toggleExpanded(node.path);
                            }}
                            className={`tree-node-toggle ${hasChildren ? "" : "tree-node-toggle-empty"}`}
                        >
                            {expanded.has(node.path) ? <ArrowDown size={14} /> : <ArrowRight size={14} />}
                        </span>
                        <span
                            className={`tree-node-label ${node.isKey ? "tree-node-label-mono" : "tree-node-label-muted"} ${unregistered ? "tree-node-label-muted" : ""}`}
                            title={node.path}
                        >
                            {node.segment}{unsaved ? " *" : ""}
                        </span>
                        {skillId && onOpenKvp && (
                            <Pill
                                tone="accent"
                                title={appState.t("settings.kvp.pill.kvpTitle")}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenKvp(`skill.${skillId}`);
                                }}
                            >
                                {appState.t("settings.kvp.pill.kvp")}
                            </Pill>
                        )}
                    </div>
                );
            })}
        </div>
    );
});

export const I18nEditor = observer(({
    i18n,
    kvpRows,
    onOpenKvp,
    onDeleted,
}: {
    i18n: I18nAdminState;
    kvpRows?: { key: string; value: string }[];
    onOpenKvp?: (key: string) => void;
    onDeleted?: () => void;
}) => {
    const selected = i18n.selected;
    const kvpLeaves = selected
        ? kvpLeavesForI18nKey(selected.key.trim(), kvpRows ?? [])
        : [];
    const selectedPersistedKey = i18n.originalKey?.trim() || selected?.key.trim() || "";
    const canDelete = !!selected && (
        !selectedPersistedKey
        || i18n.persistedKeys.has(selectedPersistedKey)
        || !EXPECTED_I18N_KEY_SET.has(selectedPersistedKey)
    );

    return (
        <div className="flex flex-col gap-2 w-full min-h-[24rem]">
            <div className="flex flex-row items-center justify-between gap-1">
                <div className="inspector-header">
                    <p className="eyebrow">{appState.t("settings.i18n")}</p>
                    <p className="h2 truncate font-mono">
                        {selected?.key.trim() || appState.t("settings.i18n.newKey")}
                    </p>
                </div>
                <ActionHandler
                    title={appState.t("settings.i18n.reload")}
                    funq={() => i18n.load()}
                    disabled={i18n.loading || i18n.saving}
                >
                    {appState.t("settings.i18n.reload")}
                </ActionHandler>
            </div>

            {i18n.loading && (
                <p className="detail-text">{appState.t("common.loading")}</p>
            )}
            {i18n.error && (
                <p className="error-text">{i18n.error}</p>
            )}

            {selected ? (
                <div key={(i18n.originalKey ?? selected.key) || "draft"} className="flex flex-col gap-2">
                    <label className="field">
                        <small className="field-label">{appState.t("settings.kvp.key")}</small>
                        <input
                            className="input input-mono"
                            value={selected.key}
                            onChange={(e) => i18n.updateSelectedKey(e.target.value)}
                            placeholder={appState.t("settings.i18n.keyPlaceholder")}
                        />
                    </label>
                    <SettingsKeyLinks
                        label={appState.t("settings.kvp.kvpLinks")}
                        keys={kvpLeaves}
                        onOpen={onOpenKvp}
                    />
                    {i18n.locales.map((locale) => {
                        const value = selected.values[locale.code] ?? "";
                        const missing = !Object.prototype.hasOwnProperty.call(selected.values, locale.code)
                            || selected.values[locale.code] === undefined;
                        return (
                            <label key={locale.code} className="field min-h-0">
                                <small className="field-label">
                                    {locale.name} ({locale.code})
                                    {missing && !value ? ` · ${appState.t("settings.i18n.missing")}` : ""}
                                </small>
                                <textarea
                                    className="textarea input-mono min-h-[5.5rem]"
                                    value={value}
                                    onChange={(e) => i18n.updateSelectedValue(locale.code, e.target.value)}
                                />
                            </label>
                        );
                    })}
                    <div className="flex flex-row gap-[.25rem] shrink-0">
                        <ActionHandler
                            title={appState.t("common.save")}
                            funq={i18n.saveSelected}
                            disabled={i18n.loading || i18n.saving}
                        >
                            {i18n.saving ? appState.t("common.saving") : appState.t("common.save")}
                        </ActionHandler>
                        <ActionHandler
                            title={appState.t("common.delete")}
                            funq={() => i18n.deleteSelected(onDeleted)}
                            disabled={i18n.loading || i18n.saving || !canDelete}
                            className="btn-danger"
                        >
                            {appState.t("common.delete")}
                        </ActionHandler>
                    </div>
                </div>
            ) : (
                <p className="inspector-empty-text">{appState.t("settings.i18n.selectKey")}</p>
            )}
        </div>
    );
});
